import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ApiClientError } from '../api/http'
import { getChannelConnection, updateChannelConnectionSettings } from '../channels/channelsApi'
import type { ChannelConnection, ChannelConnectionStatus, ChannelProvider } from '../channels/types'
import {
  Button,
  Card,
  ChannelDot,
  Icon,
  I,
  MOBILE_FULL_WIDTH_BUTTON_STYLE,
  MOBILE_SINGLE_COLUMN,
  Pill,
  TEXT_WRAP_STYLE,
  Toggle,
} from '../components/ui'
import { useIsMobileViewport } from '../hooks/useIsMobileViewport'

interface ChannelConnectionDetailPageProps {
  accessToken: string
  channelConnectionId: number | null
  onBack: () => void
}

type PillTone = 'neutral' | 'success' | 'warning' | 'danger'

const PROVIDER_DOT_NAMES: Record<ChannelProvider, string> = {
  ZID: 'Zid',
  SALLA: 'Salla',
  AMAZON: 'Amazon',
  TRENDYOL: 'Trendyol',
  NOON: 'Noon',
}

function statusTone(status: ChannelConnectionStatus): PillTone {
  if (status === 'CONNECTED') return 'success'
  if (status === 'PENDING') return 'warning'
  if (status === 'AUTH_EXPIRED' || status === 'ERROR') return 'danger'
  return 'neutral'
}

function isNotFound(error: unknown): boolean {
  return error instanceof ApiClientError && error.status === 404
}

export function ChannelConnectionDetailPage({ accessToken, channelConnectionId, onBack }: ChannelConnectionDetailPageProps) {
  const { t } = useTranslation()
  const isMobile = useIsMobileViewport()
  const loadRequestVersion = useRef(0)
  const saveRequestVersion = useRef(0)
  const [connection, setConnection] = useState<ChannelConnection | null>(null)
  const [loading, setLoading] = useState(false)
  const [loadError, setLoadError] = useState<unknown>(null)
  const [savedRefreshOrderBeforeZohoSend, setSavedRefreshOrderBeforeZohoSend] = useState(false)
  const [draftRefreshOrderBeforeZohoSend, setDraftRefreshOrderBeforeZohoSend] = useState(false)
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState<unknown>(null)
  const [saveNotice, setSaveNotice] = useState(false)
  const [loadAttempt, setLoadAttempt] = useState(0)

  useEffect(() => {
    const loadVersion = ++loadRequestVersion.current
    saveRequestVersion.current += 1

    // Clear route-specific data and cancel any save from the previous route/token.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setConnection(null)
    setSaving(false)
    setLoading(channelConnectionId != null)
    setLoadError(null)
    setSaveError(null)
    setSaveNotice(false)

    if (channelConnectionId == null) {
      return () => {
        loadRequestVersion.current += 1
        saveRequestVersion.current += 1
      }
    }

    getChannelConnection(accessToken, channelConnectionId)
      .then(result => {
        if (loadRequestVersion.current !== loadVersion) return
        const enabled = result.settings?.refreshOrderBeforeZohoSend ?? false
        setConnection(result)
        setSavedRefreshOrderBeforeZohoSend(enabled)
        setDraftRefreshOrderBeforeZohoSend(enabled)
      })
      .catch(error => {
        if (loadRequestVersion.current === loadVersion) setLoadError(error)
      })
      .finally(() => {
        if (loadRequestVersion.current === loadVersion) setLoading(false)
      })

    return () => {
      loadRequestVersion.current += 1
      saveRequestVersion.current += 1
    }
  }, [accessToken, channelConnectionId, loadAttempt])

  const save = async () => {
    if (channelConnectionId == null || saving) return
    const saveVersion = ++saveRequestVersion.current
    setSaving(true)
    setSaveError(null)
    setSaveNotice(false)
    try {
      const result = await updateChannelConnectionSettings(accessToken, channelConnectionId, {
        refreshOrderBeforeZohoSend: draftRefreshOrderBeforeZohoSend,
      })
      if (saveRequestVersion.current !== saveVersion) return
      const enabled = result.settings?.refreshOrderBeforeZohoSend ?? false
      setConnection(result)
      setSavedRefreshOrderBeforeZohoSend(enabled)
      setDraftRefreshOrderBeforeZohoSend(enabled)
      setSaveNotice(true)
    } catch (error) {
      if (saveRequestVersion.current === saveVersion) setSaveError(error)
    } finally {
      if (saveRequestVersion.current === saveVersion) setSaving(false)
    }
  }

  const fullWidthButtonStyle = isMobile ? MOBILE_FULL_WIDTH_BUTTON_STYLE : undefined

  if (channelConnectionId == null || isNotFound(loadError)) {
    return (
      <div style={{ display: 'grid', gap: 'var(--sp-4)' }}>
        <Button kind="ghost" size="sm" icon={I.arrowL} onClick={onBack} style={fullWidthButtonStyle}>
          {t('pages.channelConnectionDetail.back')}
        </Button>
        <Card padding={24}>
          <div style={{ fontWeight: 800, color: 'var(--fg)' }}>{t('pages.channelConnectionDetail.notFoundTitle')}</div>
          <div style={{ marginTop: 'var(--sp-2)', color: 'var(--fg-muted)', fontSize: 'var(--fs-13)', ...TEXT_WRAP_STYLE }}>
            {t('pages.channelConnectionDetail.notFoundBody')}
          </div>
        </Card>
      </div>
    )
  }

  if (loading && connection == null) {
    return (
      <div style={{ display: 'grid', gap: 'var(--sp-4)' }}>
        <Button kind="ghost" size="sm" icon={I.arrowL} onClick={onBack} style={fullWidthButtonStyle}>
          {t('pages.channelConnectionDetail.back')}
        </Button>
        <Card padding={24}>
          <div style={{ color: 'var(--fg-muted)', fontWeight: 700 }}>{t('pages.channelConnectionDetail.loading')}</div>
        </Card>
      </div>
    )
  }

  if (loadError != null || connection == null) {
    return (
      <div style={{ display: 'grid', gap: 'var(--sp-4)' }}>
        <Button kind="ghost" size="sm" icon={I.arrowL} onClick={onBack} style={fullWidthButtonStyle}>
          {t('pages.channelConnectionDetail.back')}
        </Button>
        <Card padding={24}>
          <div style={{ color: 'var(--danger)', fontWeight: 800 }}>{t('pages.channelConnectionDetail.errors.generic')}</div>
          <Button kind="secondary" size="sm" icon={I.refresh} onClick={() => setLoadAttempt(value => value + 1)} style={{ marginTop: 'var(--sp-3)', ...fullWidthButtonStyle }}>
            {t('pages.channelConnectionDetail.retry')}
          </Button>
        </Card>
      </div>
    )
  }

  const orderRefreshAvailable = connection.capabilities?.orderRefresh.enabled !== false
  const unhealthyForRefresh = connection.status !== 'CONNECTED' || !connection.adapterAvailable || !orderRefreshAvailable
  const changed = draftRefreshOrderBeforeZohoSend !== savedRefreshOrderBeforeZohoSend

  return (
    <div style={{ display: 'grid', gap: 'var(--sp-4)', minWidth: 0 }}>
      <div style={{ display: isMobile ? 'grid' : 'flex', justifyContent: 'space-between', alignItems: isMobile ? 'stretch' : 'center', gap: 'var(--sp-3)', minWidth: 0 }}>
        <Button kind="ghost" size="sm" icon={I.arrowL} onClick={onBack} style={fullWidthButtonStyle}>
          {t('pages.channelConnectionDetail.back')}
        </Button>
        <Pill tone={statusTone(connection.status)} dot>{t(`channels.status.${connection.status}`)}</Pill>
      </div>

      <Card padding={24}>
        <div style={{ display: 'grid', gap: 'var(--sp-4)', minWidth: 0 }}>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? MOBILE_SINGLE_COLUMN : 'auto minmax(0, 1fr)', gap: 'var(--sp-3)', alignItems: 'center', minWidth: 0 }}>
            <ChannelDot name={PROVIDER_DOT_NAMES[connection.provider]} size={40} />
            <div style={{ minWidth: 0 }}>
              <h2 style={{ margin: 0, color: 'var(--fg)', fontSize: 'var(--fs-20)', ...TEXT_WRAP_STYLE }}>{connection.displayName}</h2>
              <div style={{ marginTop: 'var(--sp-1)', color: 'var(--fg-muted)', fontSize: 'var(--fs-13)', ...TEXT_WRAP_STYLE }}>
                {t(`channels.providers.${connection.provider}`)} · {t('pages.channelConnectionDetail.connectionId', { id: connection.id })}
              </div>
            </div>
          </div>
          <div style={{ fontWeight: 800, color: 'var(--brand-900)' }}>{t('pages.channelConnectionDetail.identityTitle')}</div>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? MOBILE_SINGLE_COLUMN : 'repeat(3, minmax(0, 1fr))', gap: 'var(--sp-3)', minWidth: 0 }}>
            <IdentityItem label={t('pages.channelConnectionDetail.statusLabel')} value={t(`channels.status.${connection.status}`)} />
            <IdentityItem label={t('pages.channelConnectionDetail.adapterLabel')} value={t(connection.adapterAvailable ? 'channels.adapter.available' : 'channels.adapter.comingLater')} />
            <IdentityItem label={t('pages.channelConnectionDetail.orderRefreshLabel')} value={t(orderRefreshAvailable ? 'pages.channelConnectionDetail.available' : 'pages.channelConnectionDetail.unavailable')} />
          </div>
        </div>
      </Card>

      <Card padding={24}>
        <div style={{ display: 'grid', gap: 'var(--sp-4)', minWidth: 0 }}>
          <div>
            <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) auto', gap: 'var(--sp-3)', alignItems: 'center', minWidth: 0 }}>
              <div style={{ minWidth: 0 }}>
                <h2 style={{ margin: 0, color: 'var(--fg)', fontSize: 'var(--fs-18)', ...TEXT_WRAP_STYLE }}>{t('pages.channelConnectionDetail.safeguardsTitle')}</h2>
                <div style={{ marginTop: 'var(--sp-2)', color: 'var(--fg)', fontWeight: 700, ...TEXT_WRAP_STYLE }}>
                  {t('pages.channelConnectionDetail.preSendRefreshLabel')}
                </div>
              </div>
              <Toggle
                on={draftRefreshOrderBeforeZohoSend}
                onChange={value => {
                  setDraftRefreshOrderBeforeZohoSend(value)
                  setSaveError(null)
                  setSaveNotice(false)
                }}
                disabled={saving}
                ariaLabel={t('pages.channelConnectionDetail.preSendRefreshLabel')}
              />
            </div>
            <p style={{ margin: 'var(--sp-3) 0 0', color: 'var(--fg-muted)', fontSize: 'var(--fs-13)', lineHeight: 'var(--lh-relaxed)', ...TEXT_WRAP_STYLE }}>
              {t('pages.channelConnectionDetail.preSendRefreshDescription')}
            </p>
          </div>

          {draftRefreshOrderBeforeZohoSend && unhealthyForRefresh && (
            <div role="alert" style={{ display: 'grid', gridTemplateColumns: 'auto minmax(0, 1fr)', gap: 'var(--sp-2)', alignItems: 'start', padding: 'var(--sp-3)', border: '1px solid var(--warning-100)', borderRadius: 'var(--r-md)', background: 'var(--warning-100)', color: 'var(--warning)', minWidth: 0 }}>
              <Icon size={18} color="var(--warning)">{I.alert}</Icon>
              <div style={{ fontSize: 'var(--fs-13)', fontWeight: 700, ...TEXT_WRAP_STYLE }}>{t('pages.channelConnectionDetail.unavailableWarning')}</div>
            </div>
          )}

          {saveNotice && (
            <div role="status" style={{ color: 'var(--success)', fontSize: 'var(--fs-13)', fontWeight: 800 }}>
              {t('pages.channelConnectionDetail.saved')}
            </div>
          )}
          {saveError != null && (
            <div role="alert" style={{ display: 'grid', gap: 'var(--sp-2)' }}>
              <div style={{ color: 'var(--danger)', fontSize: 'var(--fs-13)', fontWeight: 800 }}>{t('pages.channelConnectionDetail.errors.generic')}</div>
              <Button kind="secondary" size="sm" icon={I.refresh} onClick={() => void save()} disabled={saving} style={fullWidthButtonStyle}>
                {t('pages.channelConnectionDetail.retry')}
              </Button>
            </div>
          )}
          <div style={{ display: isMobile ? 'grid' : 'flex', justifyContent: 'flex-end' }}>
            <Button kind="primary" size="sm" onClick={() => void save()} disabled={!changed || saving} style={fullWidthButtonStyle}>
              {saving ? t('pages.channelConnectionDetail.saving') : t('pages.channelConnectionDetail.save')}
            </Button>
          </div>
        </div>
      </Card>
    </div>
  )
}

function IdentityItem({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ minWidth: 0, padding: 'var(--sp-3)', borderRadius: 'var(--r-md)', background: 'var(--ink-50)' }}>
      <div style={{ color: 'var(--fg-subtle)', fontSize: 'var(--fs-12)', fontWeight: 800, textTransform: 'uppercase', letterSpacing: 'var(--ls-eyebrow)' }}>{label}</div>
      <div style={{ marginTop: 'var(--sp-1)', color: 'var(--fg)', fontSize: 'var(--fs-13)', fontWeight: 700, ...TEXT_WRAP_STYLE }}>{value}</div>
    </div>
  )
}
